import { Theme } from '@radix-ui/themes';
import {
  createContext,
  type ReactNode,
  use,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';

/** User preference. `system` follows the OS setting. */
export type ThemeMode = 'light' | 'dark' | 'system';
/** Concrete appearance handed to Radix Theme. */
export type Appearance = 'light' | 'dark';

export const THEME_STORAGE_KEY = 'ops-desk-theme';

interface ThemeModeContextValue {
  mode: ThemeMode;
  appearance: Appearance;
  setMode: (mode: ThemeMode) => void;
  toggle: () => void;
}

const ThemeModeContext = createContext<ThemeModeContextValue | null>(null);

function getSystemAppearance(): Appearance {
  return typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-color-scheme: dark)').matches
    ? 'dark'
    : 'light';
}

function readStoredMode(): ThemeMode {
  try {
    const saved = localStorage.getItem(THEME_STORAGE_KEY);
    return saved === 'light' || saved === 'dark' ? saved : 'system';
  } catch {
    return 'system';
  }
}

/**
 * Owns the light/dark preference, keeps the documentElement class and Radix appearance in
 * sync, and persists the choice. `/theme-init.js` sets the initial class before first paint.
 */
export function ThemeModeProvider({ children }: { children: ReactNode }) {
  const [mode, setModeState] = useState<ThemeMode>(readStoredMode);
  const [systemAppearance, setSystemAppearance] = useState<Appearance>(getSystemAppearance);

  useEffect(() => {
    if (typeof window.matchMedia !== 'function') return;
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const onChange = () => setSystemAppearance(mq.matches ? 'dark' : 'light');
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  const appearance: Appearance = mode === 'system' ? systemAppearance : mode;

  useEffect(() => {
    const root = document.documentElement;
    root.classList.remove('light', 'dark');
    root.classList.add(appearance);
  }, [appearance]);

  const setMode = useCallback((next: ThemeMode) => {
    setModeState(next);
    try {
      if (next === 'system') localStorage.removeItem(THEME_STORAGE_KEY);
      else localStorage.setItem(THEME_STORAGE_KEY, next);
    } catch {
      // Storage unavailable: the preference just won't persist.
    }
  }, []);

  const toggle = useCallback(
    () => setMode(appearance === 'dark' ? 'light' : 'dark'),
    [appearance, setMode],
  );

  const value = useMemo(
    () => ({ mode, appearance, setMode, toggle }),
    [mode, appearance, setMode, toggle],
  );

  return (
    <ThemeModeContext.Provider value={value}>
      {/* brand-theme.css remaps --accent-* / --gray-* on .radix-themes.light/.dark. */}
      <Theme
        appearance={appearance}
        accentColor="crimson"
        grayColor="slate"
        radius="medium"
        scaling="110%"
      >
        {children}
      </Theme>
    </ThemeModeContext.Provider>
  );
}

export function useThemeMode(): ThemeModeContextValue {
  const ctx = use(ThemeModeContext);
  if (!ctx) throw new Error('useThemeMode must be used within a ThemeModeProvider');
  return ctx;
}
